import { isUsingCustomQueueSelector } from './../utils/custom-field-utils';
import { convertScheduleDayToFormDay } from './../components/pages/manage/self-check-in/utils';
import { useEffect, useMemo } from "react"
import { useTypedSelector } from "../store"
import useSelfCheckIns from "./api-hooks/useSelfCheckIns"
import { updateSelfCheckIn } from "@/api/nextme/self-check-ins"
import { CustomField } from "@/api/interface/custom-field"
import { SelfCheckIn } from "@/api/interface/self-check-in"
import useSciCustomFields from "./useSciCustomFields"
import { buildEmptySci } from "@/components/pages/manage/self-check-in/utils"
import useSelectedLocation from "./api-selected-hooks/useSelectedLocation"
import useBusiness from "./api-hooks/useBusiness"
import useIsUsingCustomQueueSelector from './useIsUsingCustomQueueSelector';

/**
 * This should do the same thing that is being done on SCI settings
 * Rework to use action/reducer and handle sci manipulation in @/store/middleware/sciMiddleware.ts
 */

const useAddCFToSCI = () => {
  const { selfCheckIns, mutateSelfCheckIns } = useSelfCheckIns()
  const { businessId } = useTypedSelector((state) => state.selected)
  const { isUsingSharedSettings, hasOnlyOneLocation } = useTypedSelector(
    (state) => state.pages.manage
  )
  const { business } = useBusiness()
  const {locationId, selectedLocation} = useSelectedLocation()

  const {
    possibleCFs,
  } = useSciCustomFields(selfCheckIns)

  const { isUsingCustomQueueSelector } = useIsUsingCustomQueueSelector(true)

  const timezone = useMemo(
    () => selectedLocation?.timezone || business?.timezone,
    [selectedLocation, business]
  )

  const shouldUseSharedSettings = useMemo(
    () => hasOnlyOneLocation || isUsingSharedSettings,
    [hasOnlyOneLocation, isUsingSharedSettings]
  )
  
  const emptySci = useMemo(
    () =>
      buildEmptySci(
        businessId,
        locationId,
        possibleCFs,
        timezone,
        selectedLocation?.address?.id,
        isUsingCustomQueueSelector,
      ),
    [locationId, possibleCFs, selectedLocation, business, isUsingCustomQueueSelector]
  )

  const hydrateSelfCheckIn = (sci: SelfCheckIn) =>
    sci
      ? {
        ...sci,
        settings: { ...emptySci.settings, ...sci.settings },
        scheduleDays: sci.schedule?.items?.map((x) =>
          convertScheduleDayToFormDay(x)
        ),
        geofence: { ...emptySci.geofence, ...sci.geofence },
      }
      : null

  const sciToUpdate = useMemo(() => {
    if (shouldUseSharedSettings) {
      return hydrateSelfCheckIn(selfCheckIns?.filter((sci) => sci.sciableType === "business")[0])
    }
    if (!shouldUseSharedSettings) {
      return hydrateSelfCheckIn(selfCheckIns?.filter((sci) => sci.sciableId === locationId && sci.deletedAt === null)[0])
    }
    // instead of returning null, can build an empty sci?
    return null
  }, [selfCheckIns, locationId, isUsingCustomQueueSelector])

  async function updateSci(cf: CustomField, sciToUpdate: SelfCheckIn) {
    const getSettingsToUpdate = () => {
      if (sciToUpdate) {
        const currentSettings = sciToUpdate?.settings
        const update = { [cf.id]: cf.visibility === 'all' || cf.visibility === 'self_check_in' }
        return { ...currentSettings.customFields, ...update }
      }
    }

    const getUpdatedSci = () => {
      if (sciToUpdate) {
        let newSci = { ...sciToUpdate }
        newSci.settings.customFields = getSettingsToUpdate()
        newSci.settings.publicFields.queueSelect = !isUsingCustomQueueSelector && cf.type !== 'queueselect'
        return newSci
      }
    }

    const updatedSci = getUpdatedSci()

    if (updatedSci) {
      try {
        const savedSci = await updateSelfCheckIn(updatedSci)
      } catch (e) {
        console.error(e)
      }
    }

    return false
  }

  async function resetPublicQueueSelector(sciToUpdate: SelfCheckIn) {
    const updatedSci = { ...sciToUpdate }
    updatedSci.settings.publicFields.queueSelect = true
    if (updatedSci) {
      try {
        await updateSelfCheckIn(updatedSci)
      } catch (e) {
        console.error(e)
      }
    }
  }

  return {
    updateSci,
    sciToUpdate,
    mutateSelfCheckIns,
    resetPublicQueueSelector,
  }
}

export default useAddCFToSCI
